import type {
  AxiosBaseQueryArgs,
  AxiosBaseQueryError,
} from '@gasbi/shared/common/utils/axiosBaseQuery';
import type {
  BaseQueryFn,
  EndpointBuilder,
  EndpointDefinitions,
} from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

interface SupplySliceExtraOptions {
  /** The slice's url prefix, put in front of every endpoint url. */
  baseUrl?: string;
}

const supplyBaseQuery: BaseQueryFn<
  AxiosBaseQueryArgs,
  unknown,
  AxiosBaseQueryError,
  SupplySliceExtraOptions
> = (args, api, { baseUrl = '' } = {}) =>
  axiosBaseQuery({ baseUrl })(args, api, {});

/**
 * The single RTK Query API of the Supply UI's own features (GSB-7815, the pattern of the BMS fix
 * GSB-7788). Each createApi adds a middleware, and on the first action every middleware dispatches
 * `middlewareRegistered` through the whole chain again, so the stack grows with the square of their
 * count: BMS overflowed it with 138 APIs, this app had 56 (49 own + 7 from @gasbi/shared). Features inject
 * their endpoints here with injectSupplySlice instead of calling createApi.
 */
export const supplyApi = createApi({
  reducerPath: 'supplyApi',
  baseQuery: supplyBaseQuery,
  endpoints: () => ({}),
});

interface SupplySliceOptions<
  Definitions extends EndpointDefinitions,
  TagTypes extends string,
> {
  /** Url prefix of the slice's endpoints, the baseUrl its own createApi used to get. */
  baseUrl: string;
  tagTypes?: readonly TagTypes[];
  /** Default cache lifetime of the slice's queries; an endpoint's own value wins. */
  keepUnusedDataFor?: number;
  endpoints: (
    build: EndpointBuilder<
      typeof supplyBaseQuery,
      TagTypes,
      typeof supplyApi.reducerPath
    >,
  ) => Definitions;
}

/**
 * Injects a feature's endpoints into supplyApi, taking the options its own createApi used to take.
 * Endpoint names share one namespace across the app, so a duplicate name throws at startup
 * instead of silently keeping the first definition.
 */
export const injectSupplySlice = <
  Definitions extends EndpointDefinitions,
  TagTypes extends string = never,
>({
  baseUrl,
  tagTypes = [],
  keepUnusedDataFor,
  endpoints,
}: SupplySliceOptions<Definitions, TagTypes>) =>
  supplyApi.enhanceEndpoints({ addTagTypes: tagTypes }).injectEndpoints({
    endpoints: build =>
      Object.fromEntries(
        Object.entries(endpoints(build)).map(([name, definition]) => [
          name,
          {
            keepUnusedDataFor,
            ...definition,
            extraOptions: {
              baseUrl,
              ...(definition.extraOptions as
                | SupplySliceExtraOptions
                | undefined),
            },
          },
        ]),
      ) as Definitions,
    overrideExisting: 'throw',
  });
